import api from '../client';

// Interfaces
export interface Category {
  uuid: string;
  name: string;
  description: string;
  is_active: boolean;
  sort_order: number;
}

export interface Charity {
  uuid: string;
  category_uuid: string;
  category: Category;
  name: string;
  abn: string;
  contact_name?: string | null;
  contact_email: string;
  contact_phone: string;
  website: string | null;
  head_office_location: string;
  charity_government_id: string;
  dgr_tax_status?: string | null;
  description?: string | null;
  verified: number;
  rejected?: boolean;
  /** Computed admin status: 'verified' | 'invited' | 'rejected' | 'not_verified' */
  registration_status?: 'verified' | 'invited' | 'rejected' | 'not_verified';
  verified_bsb_no: string;
  verified_acc_no: string;
  verified_acc_name: string;
  bank_details_verified_at?: string | null;
  bank_details_verified_by_user_uuid?: string | null;
  /** Admin update payload: toggle phone/bank verification (not always present on GET) */
  bank_details_verified?: boolean;
  bank_details_status?: 'verified' | 'unverified';
  created_at?: string;
  updated_at: string;
  logo?: string;
  total_donation_received: {
    total_donation_received: number;
  };
}

export interface CharityFilters {
  category_uuid?: string;
  phrase?: string;
  page?: number;
  per_page?: number;
  verified?: number; // 0 or 1 for verification status filter
  /** 1 to show only invited-but-not-registered charities */
  invited?: number;
  /** true / false / 1 / 0 — bank details phone-verified by admin */
  bank_details_verified?: boolean | number | string;
  /** When true, only charities with BSB/account details on file */
  has_bank_details?: boolean | number | string;
  rejected?: number; // 1 to filter by rejected only
  start_date?: string; // Registration date start
  end_date?: string; // Registration date end
  sort_by?: string; // 'created_at' | 'name'
  sort_direction?: 'asc' | 'desc';
}

export interface PaginationInfo {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

export interface CharityResponse {
  success: boolean;
  message: string;
  data: Charity[];
  paging: PaginationInfo;
  errors?: { [key: string]: string[] };
}

export class CharityService {
  private static readonly baseUrl = '/charity';

  /**
   * Get list of charities with optional filters
   */
  static async getCharities(filters?: CharityFilters): Promise<CharityResponse> {
    try {
      const response = await api.get<CharityResponse>(this.baseUrl, { params: filters });
      const raw = response.data as { success: boolean; data: { items?: unknown[]; paging?: unknown } | unknown[]; paging?: unknown };
      const d = raw.data;
      const data = (d && typeof d === 'object' && 'items' in d ? d.items : Array.isArray(d) ? d : []) ?? [];
      const paging = (d && typeof d === 'object' && 'paging' in d ? d.paging : raw.paging) ?? undefined;
      return { ...raw, data, paging } as CharityResponse;
    } catch (error: any) {
      // Handle specific error cases
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      if (error.response?.status === 403) {
        throw new Error('Permission denied');
      }
      if (error.response?.status === 422) {
        throw new Error('Invalid input data');
      }
      throw new Error(error.response?.data?.message || 'Failed to fetch charities');
    }
  }

  /**
   * Get a single charity by UUID
   */
  static async getCharityByUuid(uuid: string): Promise<CharityResponse> {
    try {
      const response = await api.get<CharityResponse>(`${this.baseUrl}/${uuid}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch charity details');
    }
  }

  /**
   * Create a new charity
   */
  static async createCharity(data: Partial<Charity>): Promise<CharityResponse | any> {
    try {
      const response = await api.post<CharityResponse>(this.baseUrl, data);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 422) {
        // Return the error response instead of throwing
        return {
          success: false,
          message: error.response.data.message || 'Validation failed',
          data: error.response.data.data || error.response.data.errors || {},
          error: error
        };
      }
      // Return a formatted error object
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create charity',
        error: error
      };
    }
  }

  /**
   * Update an existing charity
   */
  static async updateCharity(uuid: string, data: Partial<Charity>): Promise<CharityResponse | any> {
    try {
      const response = await api.put<CharityResponse>(`${this.baseUrl}/${uuid}`, data);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 422) {
        // Return the error response instead of throwing
        return {
          success: false,
          message: error.response.data.message || 'Validation failed',
          data: error.response.data.data || error.response.data.errors || {},
          error: error
        };
      }
      // Return a formatted error object
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update charity',
        error: error
      };
    }
  }

  /**
   * Upload or replace a charity logo.
   */
  static async updateCharityLogo(uuid: string, logo: File): Promise<CharityResponse | any> {
    try {
      const formData = new FormData();
      formData.append('logo', logo);

      const response = await api.post<CharityResponse>(`${this.baseUrl}/${uuid}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update charity logo',
        error
      };
    }
  }

  /**
   * Delete a charity
   */
  static async deleteCharity(uuid: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`${this.baseUrl}/${uuid}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete charity');
    }
  }

  /**
   * Format currency values
   */
  static formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(value);
  }

  /**
   * Format date values
   */
  static formatDate(date: string | null): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-AU');
  }

  /**
   * Get verification status text
   */
  static getVerificationStatus(verified: number): string {
    return verified === 1 ? 'Verified' : 'Not Verified';
  }

  /**
   * Get verification status color
   */
  static getVerificationStatusColor(verified: number): string {
    return verified === 1 ? '#059669' : '#dc2626';
  }
}

// Export instance for backward compatibility
export const charityService = CharityService; 